import { Send01, Check, ChevronDown, CurrencyDollar, Download01, Paperclip, XClose, FaceSmile } from '@untitled-ui/icons-react/build/cjs';
import { parsePhoneNumberFromString } from 'libphonenumber-js';
import moment from 'moment';
import 'moment-timezone';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { PhotoProvider, PhotoView } from 'react-photo-view';
import 'react-photo-view/dist/react-photo-view.css';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { AVAILABILITY_OPTIONS } from 'src/app/MyQuote/CreateQuote/Steps/Steps.const';
import { getDayPairLabel, getWeekRangeLabel } from 'src/app/MyQuote/CreateQuote/Steps/Steps.helper';
import csv from 'src/assets/img/FileTypeIcons/csv-lg.svg';
import defaultFileType from 'src/assets/img/FileTypeIcons/default-lg.svg';
import doc from 'src/assets/img/FileTypeIcons/doc-lg.svg';
import pdf from 'src/assets/img/FileTypeIcons/pdf-lg.svg';
import txt from 'src/assets/img/FileTypeIcons/txt-lg.svg';
import xls from 'src/assets/img/FileTypeIcons/xls-lg.svg';
import xlsx from 'src/assets/img/FileTypeIcons/xlsx-lg.svg';
import WalLogoIcon from 'src/assets/img/logo/logo-icon.svg';
import pdfAttachmentIcon from 'src/assets/img/pdf.svg';
import ActivityFeedSkeleton from 'src/components/ActivityFeedCmp/ActivityFeedSkeleton';
import ButtonCmp from 'src/components/ButtonCmp';
import EmojiPicker from 'src/components/EmojiPicker';
import InputTextArea from 'src/components/InputTextArea';
import NotFoundUI from 'src/components/NotFoundUI';
import StatusDotProfileCmp from 'src/components/StatusDotProfileCmp';
import TooltipCmp from 'src/components/TooltipCmp';
import TruncateTextCmp from 'src/components/TruncateText';
import { PATH } from 'src/constants/path';
import useOrgBranding from 'src/hooks/useOrgBranding';
import { currentUser as currentUserSelector } from 'src/redux/auth.slice';
import { appearance as appearanceSelector } from 'src/redux/basic.slice';
import { useAppDispatch, useAppSelector } from 'src/redux/hooks';
import { activitiesData, activitiesLoadingData, activitiesPaginationData, activitiesPostLoadingData, prependActivity, setActivitiesPostLoading } from 'src/redux/Quoting.slice';
import { postQuoteActivity } from 'src/services/QuoteService';
import { formatActivityFieldLabel, formatActivitySlugValue } from 'src/utils/activityFeed.helpers';
import { getDateWithSuffixFormat, getEmoji, getFormattedNumber, getShortName, onError } from 'src/utils/CommonFunctions';
import WalToast from 'src/utils/WalToast';

// ---------------------------------------------------------------------------
// Constants & helpers
// ---------------------------------------------------------------------------

const MAX_FILES = 4;
const MAX_FILE_SIZE_BYTES = 10 * 1024 * 1024;
const IMAGE_EXTENSIONS = ['jpg', 'jpeg', 'png', 'gif', 'webp', 'svg'];
const ALLOWED_EXTENSIONS = ['jpg', 'jpeg', 'png', 'gif', 'webp', 'svg', 'pdf', 'doc', 'docx', 'xls', 'xlsx', 'ppt', 'pptx', 'txt', 'csv'];

const getFileExtension = (filename?: string): string => {
    if (!filename) return '';
    const parts = filename.split('.');

    return parts.length > 1 ? parts.pop()!.toLowerCase() : '';
};

// Magic byte signatures used to detect corrupted/renamed files.
// csv, txt, svg are plain-text — no reliable binary signature, so they are omitted.
const FILE_MAGIC_BYTES: Record<string, Array<{ offset: number; bytes: number[] }>> = {
    jpg: [{ offset: 0, bytes: [0xff, 0xd8, 0xff] }],
    jpeg: [{ offset: 0, bytes: [0xff, 0xd8, 0xff] }],
    png: [{ offset: 0, bytes: [0x89, 0x50, 0x4e, 0x47, 0x0d, 0x0a, 0x1a, 0x0a] }],
    gif: [
        { offset: 0, bytes: [0x47, 0x49, 0x46, 0x38, 0x37, 0x61] }, // GIF87a
        { offset: 0, bytes: [0x47, 0x49, 0x46, 0x38, 0x39, 0x61] }, // GIF89a
    ],
    webp: [{ offset: 0, bytes: [0x52, 0x49, 0x46, 0x46] }], // RIFF header
    pdf: [{ offset: 0, bytes: [0x25, 0x50, 0x44, 0x46] }], // %PDF
    doc: [{ offset: 0, bytes: [0xd0, 0xcf, 0x11, 0xe0] }], // OLE2
    xls: [{ offset: 0, bytes: [0xd0, 0xcf, 0x11, 0xe0] }], // OLE2
    ppt: [{ offset: 0, bytes: [0xd0, 0xcf, 0x11, 0xe0] }], // OLE2
    docx: [{ offset: 0, bytes: [0x50, 0x4b, 0x03, 0x04] }], // ZIP (OOXML)
    xlsx: [{ offset: 0, bytes: [0x50, 0x4b, 0x03, 0x04] }], // ZIP (OOXML)
    pptx: [{ offset: 0, bytes: [0x50, 0x4b, 0x03, 0x04] }], // ZIP (OOXML)
};

const readFileHeader = (file: File, byteCount: number): Promise<Uint8Array> =>
    new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = (e) => resolve(new Uint8Array(e.target?.result as ArrayBuffer));
        reader.onerror = reject;
        reader.readAsArrayBuffer(file.slice(0, byteCount));
    });

// Returns true when the file's header bytes don't match its extension — likely corrupted or renamed.
const checkFileCorrupted = async (file: File): Promise<boolean> => {
    const ext = getFileExtension(file.name);
    const patterns = FILE_MAGIC_BYTES[ext];
    if (!patterns) return false; // no signature defined for this type — skip check

    try {
        const header = await readFileHeader(file, 12);

        return !patterns.some((p) => p.bytes.every((b, i) => header[p.offset + i] === b));
    } catch {
        return true; // unreadable file → treat as corrupted
    }
};

const isImageFile = (filename?: string) => IMAGE_EXTENSIONS.includes(getFileExtension(filename));

const getFileIcon = (filename?: string) => {
    const ext = getFileExtension(filename);

    switch (ext) {
        case 'pdf':
            return <img src={pdf} alt="PDF" className="w-8 h-8 object-contain flex-none" />;
        case 'xls':
            return <img src={xls} alt="Excel" className="w-8 h-8 object-contain flex-none" />;
        case 'xlsx':
            return <img src={xlsx} alt="Excel" className="w-8 h-8 object-contain flex-none" />;
        case 'doc':
        case 'docx':
            return <img src={doc} alt="Word" className="w-8 h-8 object-contain flex-none" />;
        case 'csv':
            return <img src={csv} alt="CSV" className="w-8 h-8 object-contain flex-none" />;
        case 'txt':
            return <img src={txt} alt="Text" className="w-8 h-8 object-contain flex-none" />;
        default:
            return <img src={defaultFileType} alt="File" className="w-8 h-8 object-contain flex-none" />;
    }
};

const formatFileSize = (bytes?: number): string => {
    if (!bytes || bytes < 0) return '';
    if (bytes < 1024) return `${bytes} B`;
    const kb = bytes / 1024;
    if (kb < 1024) return `${kb.toFixed(kb < 10 ? 2 : 1)} kB`;
    const mb = kb / 1024;

    return `${mb.toFixed(mb < 10 ? 2 : 1)} MB`;
};

const handleAttachmentDownload = async (url: string, filename: string) => {
    try {
        const response = await fetch(url);
        const blob = await response.blob();
        const blobUrl = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = blobUrl;
        link.download = filename;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(blobUrl);
    } catch {
        window.open(url, '_blank');
    }
};

// ---------------------------------------------------------------------------
// Sub-components
// ---------------------------------------------------------------------------

interface AvatarProps {
    imageUrl?: string;
    image?: string;
    name?: string;
}

const Avatar = ({ imageUrl, image, name }: AvatarProps) => {
    const fullUrl = imageUrl && image ? `${imageUrl}${image}` : undefined;
    const fallback = getShortName(name || 'NA');

    if (fullUrl) {
        return <img src={fullUrl} alt={name || 'actor'} onError={onError} className="table-profile-icn-circle-sm flex-none w-full h-full !rounded-md" />;
    }

    return (
        <div className="w-full h-full rounded-md border border-utilityBlack border-opacity-[0.08] flex-none flex items-center justify-center uppercase bg-primary text-white text-xs">{fallback}</div>
    );
};

// Resolve {{key}} tokens in a message string using msgParams
const resolveMessage = (message: string, msgParams: Record<string, any> = {}): string => {
    if (!message) return '';

    const resolved = message.replace(/\{\{\s*(\w+)\s*\}\}/g, (_, key) => {
        if (key === 'userId') return '';

        if (key in msgParams) {
            return String(msgParams[key] ?? '');
        }

        return `{{${key}}}`;
    });

    return resolved.replace(/\s{2,}/g, ' ').trim();
};

// System-actor activities (no human user attached) that the BE has deliberately let through the
// customer feed — e.g. a quote auto-created via a pricing rule set. These read as WAL having done
// it, not a bare "System" label — matches the "Western Alliance" fallback identity used elsewhere
// (LegDocumentsRow, CarrierAssignment, OrderStatus) for system-attributed actions.
const COMPANY_ATTRIBUTED_ACTIVITY_NAMES = new Set(['quote_created_by_rule_set']);

// companyName: org display name from useOrgBranding (appearance table) — callers pass it since
// this is a plain function, not a hook context. Defaults to the historical WAL identity.
const buildActorName = (user: any, currentUserId?: number, activityName?: string, companyName = 'Western Alliance'): string => {
    if (!user) return COMPANY_ATTRIBUTED_ACTIVITY_NAMES.has(activityName || '') ? companyName : 'System';
    if (currentUserId && user.id === currentUserId) return 'You';
    const first = user.firstName || '';
    const last = user.lastName || '';

    return `${first} ${last}`.trim() || 'System';
};

const formatTime = (date: string | null | undefined, timezone?: string): string => {
    if (!date) return '';
    const m = timezone ? moment.utc(date).tz(timezone) : moment(date);

    return m.isValid() ? m.format('hh:mm A') : '';
};

// 'Do' (not 'D') so the group heading carries an ordinal — the render wraps its suffix in a
// <sup>, matching wal-frontend-v2's activity feed.
const formatDateLabel = (date: string | null | undefined, timezone?: string): string => {
    if (!date) return 'Unknown Date';
    const m = timezone ? moment.utc(date).tz(timezone) : moment(date);

    return m.isValid() ? m.format('MMMM Do, YYYY') : 'Unknown Date';
};

// Individual address sub-fields are suppressed — only "Shipper/Consignee Full Address" is shown.
// When the address changes, the BE tracks every component separately; showing them all is noisy.
const SUPPRESSED_ADDRESS_FIELDS = new Set([
    'Shipper Street',
    'Shipper Street Number',
    'Shipper City',
    'Shipper State',
    'Shipper State Code',
    'Shipper Country',
    'Shipper Country Code',
    'Shipper Postal',
    'Consignee Street',
    'Consignee Street Number',
    'Consignee City',
    'Consignee State',
    'Consignee State Code',
    'Consignee Country',
    'Consignee Country Code',
    'Consignee Postal',
    // Show human-readable names only; the raw type codes are redundant
    'Secondary Equipment Types',
    'Equipment Type Name',
]);

// Static lookup for fixed time frame slugs
const TIME_FRAME_LABELS: Record<string, string> = Object.fromEntries(AVAILABILITY_OPTIONS.map((o) => [o.value, o.label]));

// Decodes a pickupTimeFrame / deliveryTimeFrame slug into a human-readable label.
// Handles: static slugs (today_tomorrow / this_week / next_week),
//          day_pair_YYYY-MM-DD  → "Tue 14th or Wed 15th"
//          week_range_YYYY-MM-DD → "Between Apr 14th - Apr 20th"
const decodeTimeFrameSlug = (slug: string): string => {
    if (TIME_FRAME_LABELS[slug]) return TIME_FRAME_LABELS[slug];

    if (slug.startsWith('day_pair_')) {
        const date = slug.replace('day_pair_', '');

        return getDayPairLabel(date) || slug;
    }

    if (slug.startsWith('week_range_')) {
        const date = slug.replace('week_range_', '');

        return getWeekRangeLabel(date).label || slug;
    }

    return slug;
};

// Detects YYYY-MM-DD date strings and formats them as "Apr 14th, 2026".
// Also decodes pickupTimeFrame/deliveryTimeFrame slug patterns.
const DATE_VALUE_RE = /^\d{4}-\d{2}-\d{2}(T.*)?$/;
const TIME_FRAME_SLUG_RE = /^(today_tomorrow|this_week|next_week|day_pair_|week_range_)/;
const MONTH_DATE_RE = /^[A-Za-z]{3,9}\s+\d{1,2}(st|nd|rd|th)?,?\s+\d{4}/;
const PHONE_REGEX = /(\+?\d{10,15})/g;

const formatPhonesInString = (value: string): string => {
    if (!value || typeof value !== 'string') return value;

    return value.replace(PHONE_REGEX, (raw) => {
        const candidate = raw.startsWith('+') ? raw : `+${raw}`;
        const parsed = parsePhoneNumberFromString(candidate);

        return parsed?.isValid() ? parsed.formatInternational() : raw;
    });
};

const formatValue = (val: any, field?: string): string => {
    const str = String(val ?? '');
    if (TIME_FRAME_SLUG_RE.test(str)) return decodeTimeFrameSlug(str);

    if (DATE_VALUE_RE.test(str)) {
        const m = moment(str, moment.ISO_8601, true);
        if (m.isValid()) return m.format('MMM Do, YYYY');
    }

    if (MONTH_DATE_RE.test(str)) {
        const hasTime = /\d{1,2}:\d{2}/.test(str);
        const m = moment(str, ['MMM D, YYYY h:mm A', 'MMM Do, YYYY h:mm A', 'MMM D, YYYY', 'MMM Do, YYYY'], true);
        if (m.isValid()) return hasTime ? m.format('MMM Do, YYYY h:mm A') : m.format('MMM Do, YYYY');
    }

    const fieldLower = field?.toLowerCase() || '';

    if (fieldLower.includes('phone') && !fieldLower.includes('extension')) {
        return formatPhonesInString(str);
    }

    // Enum values reach the feed as the raw column slug ("ready_time", "flexible", "open") because
    // the change record stores what was written, not the form's display label. Title-case them per
    // comma-separated part so a list of slugs reads the same as a single one; formatActivitySlugValue
    // returns anything that isn't a pure lower-case slug untouched, and the original spacing is kept
    // for those so this stays a no-op for every value that already rendered correctly.
    return str
        .split(',')
        .map((part) => {
            const trimmed = part.trim();
            const formatted = formatActivitySlugValue(trimmed);

            return formatted === trimmed ? part : formatted;
        })
        .join(', ');
};

type IActivityContact = { contactName?: string; countryCode?: string; phoneNumber?: string };

const tryParseContacts = (val: any): IActivityContact[] | null => {
    let arr: any = val;

    if (typeof val === 'string') {
        const trimmed = val.trim();
        if (!trimmed.startsWith('[')) return null;

        try {
            arr = JSON.parse(trimmed);
        } catch {
            return null;
        }
    }

    if (!Array.isArray(arr) || arr.length === 0) return null;
    const isContacts = arr.every((item: any) => item && typeof item === 'object' && ('contactName' in item || 'phoneNumber' in item));

    return isContacts ? (arr as IActivityContact[]) : null;
};

const ContactsTable = ({ contacts }: { contacts: IActivityContact[] }) => (
    <div className="w-fit max-w-full overflow-x-auto rounded-md border border-utilityGray200">
        <table className="text-[11px] leading-[16px]">
            <thead className="bg-gray50">
                <tr>
                    <th className="px-2 py-1 text-left font-medium text-textSecondary whitespace-nowrap">Contact Name</th>
                    <th className="px-2 py-1 text-left font-medium text-textSecondary whitespace-nowrap">Country Code</th>
                    <th className="px-2 py-1 text-left font-medium text-textSecondary whitespace-nowrap">Phone Number</th>
                </tr>
            </thead>
            <tbody>
                {contacts.map((c, i) => (
                    <tr key={i} className="border-t border-utilityGray200">
                        <td className="px-2 py-1 text-grayLight900 whitespace-nowrap">{c.contactName || '-'}</td>
                        <td className="px-2 py-1 text-grayLight900 whitespace-nowrap">{c.countryCode ? `+${c.countryCode}` : '-'}</td>
                        <td className="px-2 py-1 text-grayLight900 whitespace-nowrap">{c.phoneNumber || '-'}</td>
                    </tr>
                ))}
            </tbody>
        </table>
    </div>
);

// Parses the BE changes object { "Display Label": { old, new } } into an array
const parseChanges = (raw?: Record<string, { old: any; new: any }> | any[]) => {
    if (!raw) return [];

    // Order-flow BE sends an array of { field, old, new, actionType } objects.
    // Normalize to the same { field, oldValue, newValue } shape the render loop expects
    // so diff chips render correctly instead of showing blank values.
    if (Array.isArray(raw)) {
        return raw
            .map((c: any) => ({
                field: formatActivityFieldLabel(c?.field ?? ''),
                oldValue: formatValue(c?.old !== undefined ? c.old : c?.oldValue, c?.field),
                newValue: formatValue(c?.new !== undefined ? c.new : c?.newValue, c?.field),
                oldContacts: tryParseContacts(c?.old !== undefined ? c.old : c?.oldValue),
                newContacts: tryParseContacts(c?.new !== undefined ? c.new : c?.newValue),
            }))
            .filter((c) => c.field && !(c.oldValue === '' && c.newValue === ''));
    }

    return Object.entries(raw)
        .filter(([field]) => !SUPPRESSED_ADDRESS_FIELDS.has(field))
        .map(([field, vals]) => ({
            field: formatActivityFieldLabel(field),
            oldValue: formatValue(vals?.old, field),
            newValue: formatValue(vals?.new, field),
            oldContacts: tryParseContacts(vals?.old),
            newContacts: tryParseContacts(vals?.new),
        }));
};

// UserActivityItem — for create / update / request activity types
const ActivityDot = () => <span className="flex-none text-[12px] leading-[18px] font-normal text-gray500">·</span>;

const UserActivityItem = ({
    activity,
    timezone,
    serviceMap,
    currentUserId,
    isReadOnly,
}: {
    activity: any;
    timezone?: string;
    serviceMap: Record<number, string>;
    currentUserId?: number;
    isReadOnly?: boolean;
}) => {
    const { t } = useTranslation();
    const orgBranding = useOrgBranding();
    const navigate = useNavigate();
    const [expanded, setExpanded] = useState(false);
    const actorMsgRef = useRef<HTMLSpanElement | null>(null);
    const fieldSummaryRef = useRef<HTMLSpanElement | null>(null);
    const changeOldValueRefs = useRef<Record<number, React.RefObject<HTMLSpanElement | null>>>({});
    const changeNewValueRefs = useRef<Record<number, React.RefObject<HTMLSpanElement | null>>>({});
    const dimNewValueRefs = useRef<Record<string, React.RefObject<HTMLSpanElement | null>>>({});
    const svcAddedRefs = useRef<Record<number, React.RefObject<HTMLSpanElement | null>>>({});
    const svcRemovedRefs = useRef<Record<number, React.RefObject<HTMLSpanElement | null>>>({});

    const actorName = buildActorName(activity.user, currentUserId, activity.activityName, orgBranding.name);
    const resolvedMsg = resolveMessage(activity.message, activity.msgParams || {});
    const time = formatTime(activity.createdAt, timezone);

    // Quote-level field changes: { "Display Label": { old, new } }
    const changes = parseChanges(activity.msgParams?.changes);

    // Dimension changes: { added, removed, modified: [{ id, changes: { "Label": { old, new } } }] }
    const dimChanges = activity.msgParams?.dimensionChanges;
    const dimModified: Array<{ id: number; changes: ReturnType<typeof parseChanges> }> = (dimChanges?.modified || []).map((m: any) => ({ id: m.id, changes: parseChanges(m.changes) }));
    const dimAdded: number = dimChanges?.added?.length || 0;
    const dimRemoved: number = dimChanges?.removed?.length || 0;

    // Service changes: { added: number[], removed: number[] }
    // Resolve IDs → names using serviceMap from Redux state.services.services
    const svcChanges = activity.msgParams?.serviceChanges;
    const svcAddedIds: number[] = svcChanges?.added || [];
    const svcRemovedIds: number[] = svcChanges?.removed || [];
    const svcAddedNames = svcAddedIds.map((id) => serviceMap[id] || `Service #${id}`);
    const svcRemovedNames = svcRemovedIds.map((id) => serviceMap[id] || `Service #${id}`);

    const hasChangesBeforeServicesAdded = changes.length > 0 || dimModified.length > 0 || dimAdded > 0 || dimRemoved > 0;
    const hasAnyChanges = changes.length > 0 || dimModified.length > 0 || dimAdded > 0 || dimRemoved > 0 || svcAddedIds.length > 0 || svcRemovedIds.length > 0;

    // Inline summary: first changed field + "+N" for the rest. Record events (added contact, uploaded
    // file, …) arrive as all-add or all-remove rows on a non-"updated" message — their rows describe
    // the record, not changes, so they get no summary. Skipped too when the message already names it.
    const isAddRow = (c: (typeof changes)[number]) => c.oldValue === '' && !c.oldContacts;
    const isRemoveRow = (c: (typeof changes)[number]) => c.newValue === '' && !c.newContacts;
    const isRecordEvent = !/\bupdated\b/i.test(String(activity.message || '')) && changes.length > 0 && (changes.every(isAddRow) || changes.every(isRemoveRow));
    const leadField = changes[0]?.field || '';
    const isLeadNamedByMessage = changes.length === 1 && resolvedMsg.toLowerCase().includes(leadField.toLowerCase());
    const showFieldSummary = !!leadField && !isRecordEvent && !isLeadNamedByMessage;

    // "<Actor> duplicated this quote from SQ-XXXX" (quote_duplicated_by_customer) — the source code
    // becomes a link back to the quote this one was duplicated from, so the customer can compare
    // the two without hunting through the list. msgParams.sourceQuoteId is the link target; the
    // code itself is already inside resolvedMsg via the {{sourceQuoteCode}} placeholder.
    //
    // Deliberately NOT linked on public share links (isReadOnly): that viewer has no session, so
    // /my-quote/:id would only bounce them to the login screen.
    const fullMessage = actorName ? `${actorName} ${resolvedMsg}` : resolvedMsg;
    const sourceQuoteId = activity?.msgParams?.sourceQuoteId;
    const sourceQuoteCode = activity?.msgParams?.sourceQuoteCode ? String(activity.msgParams.sourceQuoteCode) : '';
    const sourceCodeIdx = !isReadOnly && sourceQuoteId && sourceQuoteCode ? fullMessage.lastIndexOf(sourceQuoteCode) : -1;
    const sourceQuotePath = `${PATH.MY_QUOTE}/${sourceQuoteId}`;

    return (
        <div className="relative pb-5 flex" data-testid="order-activity-item-user">
            <p className="text-gray500 text-xs leading-[1.5] font-normal flex-none w-[55px] whitespace-nowrap" data-testid="order-activity-item-date">
                {time}
            </p>

            <span className="absolute left-[70px] top-[2px] z-10">
                <StatusDotProfileCmp dotColor="green" />
            </span>

            <div className="pl-[42px] space-y-2 overflow-hidden">
                <div className="flex flex-row gap-1 flex-wrap overflow-hidden">
                    <p
                        className={`text-[12px] leading-[18px] font-normal text-grayLight900 flex-1 min-w-0 max-w-[-webkit-fill-available] flex items-center gap-x-1 ${
                            hasAnyChanges ? 'cursor-pointer' : ''
                        }`}
                        data-testid="order-activity-item-description"
                        onClick={hasAnyChanges ? () => setExpanded((prev) => !prev) : undefined}
                    >
                        <TruncateTextCmp message={fullMessage} refName={actorMsgRef}>
                            <span ref={actorMsgRef} className="font-medium truncate block">
                                {sourceCodeIdx === -1 ? (
                                    fullMessage
                                ) : (
                                    <>
                                        {fullMessage.slice(0, sourceCodeIdx)}
                                        <span
                                            role="link"
                                            tabIndex={0}
                                            className="text-primary700 font-medium cursor-pointer"
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                navigate(sourceQuotePath);
                                            }}
                                            onKeyDown={(e) => {
                                                if (e.key === 'Enter') navigate(sourceQuotePath);
                                            }}
                                        >
                                            {sourceQuoteCode}
                                        </span>
                                        {fullMessage.slice(sourceCodeIdx + sourceQuoteCode.length)}
                                    </>
                                )}
                            </span>
                        </TruncateTextCmp>
                        {showFieldSummary && (
                            <>
                                <ActivityDot />
                                <TruncateTextCmp message={leadField} refName={fieldSummaryRef} truncateTextParentClassName="min-w-0">
                                    <span ref={fieldSummaryRef} className="text-gray500 truncate block">
                                        {leadField}
                                    </span>
                                </TruncateTextCmp>
                                {changes.length > 1 && <span className="text-gray500 flex-none whitespace-nowrap">{t('Common.Plus_Count', { count: changes.length - 1 })}</span>}
                            </>
                        )}
                        {hasAnyChanges && (
                            <span className="inline-flex items-center text-gray500 flex-none">
                                <ChevronDown className={`w-3.5 h-3.5 min-w-3.5 min-h-3.5 transition-transform duration-300 ${expanded ? 'rotate-180' : 'rotate-0'}`} />
                            </span>
                        )}
                    </p>
                </div>

                {hasAnyChanges && (
                    <div className="!mt-0 grid" style={{ gridTemplateRows: expanded ? '1fr' : '0fr', transition: 'grid-template-rows 300ms ease' }}>
                        <div className="overflow-hidden">
                            <div className="mt-3 flex flex-col gap-2.5 rounded-lg border border-utilityGray200 bg-gray50 p-2">
                                {/* Quote-level field changes */}
                                {changes.map((change, idx) => {
                                    if (!changeOldValueRefs.current[idx]) changeOldValueRefs.current[idx] = React.createRef<HTMLSpanElement>();
                                    if (!changeNewValueRefs.current[idx]) changeNewValueRefs.current[idx] = React.createRef<HTMLSpanElement>();

                                    if (change.oldContacts || change.newContacts) {
                                        return (
                                            <React.Fragment key={idx}>
                                                {idx > 0 && <div className="h-px bg-utilityGray200 flex-none" />}
                                                <div className="flex flex-col flex-wrap min-w-0">
                                                    <span className="text-[10px] font-normal leading-[14px] mb-1.5 text-textSecondary shrink-0 uppercase">{change.field}:</span>
                                                    <div className="flex flex-col gap-1.5 min-w-0">
                                                        {change.oldContacts && !change.newContacts && (
                                                            <div className="flex flex-col gap-0.5">
                                                                <span className="text-[11px] leading-[16px] font-medium text-textSecondary">Previous</span>
                                                                <ContactsTable contacts={change.oldContacts} />
                                                            </div>
                                                        )}
                                                        {change.newContacts && (
                                                            <div className="flex flex-col gap-0.5">
                                                                <span className="text-[11px] leading-[16px] font-medium text-textSecondary">Updated</span>
                                                                <ContactsTable contacts={change.newContacts} />
                                                            </div>
                                                        )}
                                                    </div>
                                                </div>
                                            </React.Fragment>
                                        );
                                    }

                                    // add → new value only, remove → old value only (struck through), update → new value only
                                    const showOld = change.newValue === '' && change.oldValue !== '';
                                    const showNew = change.newValue !== '';

                                    return (
                                        <React.Fragment key={idx}>
                                            {idx > 0 && <div className="h-px bg-utilityGray200 flex-none" />}
                                            <div className="flex flex-col flex-wrap min-w-0">
                                                <span className="text-[10px] font-normal leading-[14px] mb-1.5 text-textSecondary shrink-0 uppercase">{change.field}:</span>
                                                <span className="flex flex-wrap flex-row gap-x-4 gap-y-2 items-start max-w-[-webkit-fill-available]">
                                                    {showOld && (
                                                        <span className="flex items-start gap-x-1 min-w-0">
                                                            <span className="flex-none pt-1">
                                                                <span className="flex items-center justify-center w-2 h-2">
                                                                    <span className="w-1.5 h-1.5 rounded-full bg-gray400" />
                                                                </span>
                                                            </span>
                                                            <TruncateTextCmp
                                                                message={String(change.oldValue)}
                                                                refName={changeOldValueRefs.current[idx]}
                                                                truncateTextParentClassName="min-w-0"
                                                                tooltipType="action"
                                                            >
                                                                <span
                                                                    ref={changeOldValueRefs.current[idx]}
                                                                    className="text-[12px] leading-[18px] font-normal text-gray500 truncate block line-through"
                                                                    dangerouslySetInnerHTML={{ __html: getDateWithSuffixFormat(String(change.oldValue)) }}
                                                                />
                                                            </TruncateTextCmp>
                                                        </span>
                                                    )}
                                                    {showNew && (
                                                        <span className="flex items-start gap-x-1 min-w-0">
                                                            <span className="flex-none pt-1">
                                                                <span className="flex items-center justify-center w-2 h-2">
                                                                    <span className="w-1.5 h-1.5 rounded-full bg-gray500" />
                                                                </span>
                                                            </span>
                                                            <TruncateTextCmp
                                                                message={String(change.newValue)}
                                                                refName={changeNewValueRefs.current[idx]}
                                                                truncateTextParentClassName="min-w-0"
                                                                tooltipType="action"
                                                            >
                                                                <span
                                                                    ref={changeNewValueRefs.current[idx]}
                                                                    className="text-[12px] leading-[18px] font-medium text-textSecondary truncate block"
                                                                    dangerouslySetInnerHTML={{ __html: getDateWithSuffixFormat(String(change.newValue)) }}
                                                                />
                                                            </TruncateTextCmp>
                                                        </span>
                                                    )}
                                                </span>
                                            </div>
                                        </React.Fragment>
                                    );
                                })}

                                {/* Dimension modifications */}
                                {dimModified.map((dim, dIdx) => (
                                    <React.Fragment key={dIdx}>
                                        {(changes.length > 0 || dIdx > 0) && <div className="h-px bg-utilityGray200 flex-none" />}
                                        <div className="flex flex-col gap-2.5 min-w-0">
                                            <span className="text-[12px] font-medium leading-[18px] text-grayLight900">Item #{dim.id} updated</span>
                                            {dim.changes.map((c, cIdx) => {
                                                const refKey = `${dIdx}_${cIdx}`;
                                                if (!dimNewValueRefs.current[refKey as any]) dimNewValueRefs.current[refKey as any] = React.createRef<HTMLSpanElement>();
                                                if (c.newValue === '') return null;

                                                return (
                                                    <div key={cIdx} className="flex flex-col flex-wrap min-w-0 pl-2">
                                                        <span className="text-[10px] font-normal leading-[14px] mb-1.5 text-textSecondary shrink-0 uppercase">{c.field}:</span>
                                                        <span className="flex items-start gap-x-1 min-w-0">
                                                            <span className="flex-none pt-1">
                                                                <span className="flex items-center justify-center w-2 h-2">
                                                                    <span className="w-1.5 h-1.5 rounded-full bg-gray500" />
                                                                </span>
                                                            </span>
                                                            <TruncateTextCmp
                                                                message={String(c.newValue)}
                                                                refName={dimNewValueRefs.current[refKey as any]}
                                                                truncateTextParentClassName="min-w-0"
                                                                tooltipType="action"
                                                            >
                                                                <span
                                                                    ref={dimNewValueRefs.current[refKey as any]}
                                                                    className="text-[12px] leading-[18px] font-medium text-textSecondary truncate block"
                                                                    dangerouslySetInnerHTML={{ __html: getDateWithSuffixFormat(String(c.newValue)) }}
                                                                />
                                                            </TruncateTextCmp>
                                                        </span>
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    </React.Fragment>
                                ))}

                                {/* Dimension added / removed summary */}
                                {dimAdded > 0 && (
                                    <span className="text-[12px] leading-[18px] text-grayLight900">
                                        <span className="font-semibold">{dimAdded}</span> item{dimAdded > 1 ? 's' : ''} added
                                    </span>
                                )}
                                {dimRemoved > 0 && (
                                    <span className="text-[12px] leading-[18px] text-grayLight900">
                                        <span className="font-semibold">{dimRemoved}</span> item{dimRemoved > 1 ? 's' : ''} removed
                                    </span>
                                )}

                                {/* Service changes — show actual service names */}
                                {svcAddedNames.length > 0 && (
                                    <>
                                        {hasChangesBeforeServicesAdded && <div className="h-px bg-utilityGray200 flex-none" />}
                                        <div className="flex flex-col flex-wrap min-w-0">
                                            <span className="text-[10px] font-normal leading-[14px] mb-1.5 text-textSecondary shrink-0 uppercase">Services Added:</span>
                                            <div className="flex flex-row gap-x-4 gap-y-2 items-start flex-wrap min-w-0">
                                                {svcAddedNames.map((name, i) => {
                                                    if (!svcAddedRefs.current[i]) svcAddedRefs.current[i] = React.createRef<HTMLSpanElement>();

                                                    return (
                                                        <span key={i} className="flex items-start gap-x-1 min-w-0">
                                                            <span className="flex-none pt-1">
                                                                <span className="flex items-center justify-center w-2 h-2">
                                                                    <span className="w-1.5 h-1.5 rounded-full bg-gray500" />
                                                                </span>
                                                            </span>
                                                            <TruncateTextCmp message={name} refName={svcAddedRefs.current[i]} truncateTextParentClassName="min-w-0" tooltipType="action">
                                                                <span ref={svcAddedRefs.current[i]} className="text-[12px] leading-[18px] max-w-[380px] font-medium text-textSecondary truncate block">
                                                                    {name}
                                                                </span>
                                                            </TruncateTextCmp>
                                                        </span>
                                                    );
                                                })}
                                            </div>
                                        </div>
                                    </>
                                )}
                                {svcRemovedNames.length > 0 && (
                                    <>
                                        {(hasChangesBeforeServicesAdded || svcAddedNames.length > 0) && <div className="h-px bg-utilityGray200 flex-none" />}
                                        <div className="flex flex-col flex-wrap min-w-0">
                                            <span className="text-[10px] font-normal leading-[14px] mb-1.5 text-textSecondary shrink-0 uppercase">Services Removed:</span>
                                            <div className="flex flex-row gap-x-4 gap-y-2 items-start flex-wrap min-w-0">
                                                {svcRemovedNames.map((name, i) => {
                                                    if (!svcRemovedRefs.current[i]) svcRemovedRefs.current[i] = React.createRef<HTMLSpanElement>();

                                                    return (
                                                        <span key={i} className="flex items-start gap-x-1 min-w-0">
                                                            <span className="flex-none pt-1">
                                                                <span className="flex items-center justify-center w-2 h-2">
                                                                    <span className="w-1.5 h-1.5 rounded-full bg-gray400" />
                                                                </span>
                                                            </span>
                                                            <TruncateTextCmp message={name} refName={svcRemovedRefs.current[i]} truncateTextParentClassName="min-w-0" tooltipType="action">
                                                                <span
                                                                    ref={svcRemovedRefs.current[i]}
                                                                    className="text-[12px] leading-[18px] max-w-[380px] font-normal text-gray500 truncate block line-through"
                                                                >
                                                                    {name}
                                                                </span>
                                                            </TruncateTextCmp>
                                                        </span>
                                                    );
                                                })}
                                            </div>
                                        </div>
                                    </>
                                )}
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

const IntegrationCard = ({ activity, timezone, cardClass }: { activity: any; timezone?: string; cardClass?: string }) => {
    const orgBranding = useOrgBranding();
    const integration = activity?.integration;
    const integrationName = integration?.name || integration?.typeDisplayName || 'System';
    const integrationLogo = integration?.logoUrl && integration?.logo ? `${integration.logoUrl}${integration.logo}` : undefined;
    // A carrier integration on a hidden-carrier order arrives re-attributed to the org, flagged
    // with isOrgCarrier (same marker as a masked carrier elsewhere). It carries no logo of its
    // own, so without this it fell back to initials while Carrier Details showed the WAL icon.
    // The name comparison is the fallback for the public share page, which has no appearance data.
    const isOrgCarrier = integration?.isOrgCarrier === true || (!!integration?.name && integration.name === orgBranding.name);
    const resolvedMsg = resolveMessage(activity.message, activity.msgParams || {});
    const time = formatTime(activity.createdAt, timezone);

    const status = activity?.activityStatus;
    const showStatusBadge = status === 'success' || status === 'failed';
    const subTitleRef = useRef<HTMLParagraphElement | null>(null);

    return (
        <div className={cardClass} data-testid="order-activity-item">
            <div className="activity-box-content-wrap">
                <div className="activity-box-avatar-wrap flex-none hidden">
                    {integrationLogo ? (
                        <img src={integrationLogo} alt={integrationName} className="w-full h-full rounded-[8px] object-contain bg-white border border-utilityGray200" onError={onError} />
                    ) : isOrgCarrier ? (
                        // Org identity — uploaded brand logo, black WAL icon as fallback. Mirrors
                        // CarrierDetailsSection so the masked carrier looks the same in both places.
                        orgBranding.logoSrc ? (
                            <img src={orgBranding.logoSrc} alt={orgBranding.name} className="w-full h-full rounded-[8px] object-contain bg-white border border-utilityGray200" onError={onError} />
                        ) : (
                            <div className="w-full h-full rounded-[8px] !bg-utilityBlack flex items-center justify-center p-1">
                                <img src={WalLogoIcon} alt={orgBranding.name} className="w-full h-full object-contain" />
                            </div>
                        )
                    ) : (
                        <div className="w-full h-full rounded-[8px] bg-utilityGray100 flex items-center justify-center text-[10px] font-medium text-grayLight600 uppercase">
                            {getShortName(integrationName)}
                        </div>
                    )}
                    {showStatusBadge && (
                        <span className="activity-box-avatar-wrap-status">
                            {status === 'success' ? (
                                <Check className="activity-box-avatar-wrap-status-icon text-successSecondary flex-none" />
                            ) : (
                                <XClose className="activity-box-avatar-wrap-status-icon text-fgErrorPrimary flex-none" />
                            )}
                        </span>
                    )}
                </div>

                <p className="text-gray500 text-xs leading-[1.5] font-normal flex-none w-[55px] whitespace-nowrap" data-testid="order-activity-item-date">
                    {time}
                </p>
                <div className="activity-box-text-wrap pl-[30px] min-w-0 overflow-hidden">
                    <div className="activity-box-title">
                        <span className="flex-none" data-testid="order-activity-item-name">
                            {integrationName}
                        </span>
                        {resolvedMsg && <ActivityDot />}
                        {resolvedMsg && (
                            <TruncateTextCmp refName={subTitleRef} message={resolvedMsg} truncateTextParentClassName="min-w-0">
                                <p ref={subTitleRef} className="activity-box-sub-title truncate" data-testid="order-activity-item-description">
                                    {resolvedMsg}
                                </p>
                            </TruncateTextCmp>
                        )}
                    </div>
                </div>
                <span className="absolute left-[70px] top-[2px] z-10">
                    <StatusDotProfileCmp dotColor="green" />
                </span>
            </div>
        </div>
    );
};

// Attachments are stored in msgParams.attachments as:
// [{ file: 'uuid.ext', fileUrl: 'https://.../folder/', fileName: 'original.ext', fileType: 'ext', fileMime: '...' }]
const PostCard = ({ activity, timezone, currentUserId, containerClassName }: { activity: any; timezone?: string; currentUserId?: number; containerClassName?: string }) => {
    const { t } = useTranslation();
    const orgBranding = useOrgBranding();
    const actorName = buildActorName(activity.user, currentUserId, activity.activityName, orgBranding.name);
    const resolvedMsg = resolveMessage(activity.message, activity.msgParams || {});
    const time = formatTime(activity.createdAt, timezone);
    const attachments: any[] = activity.msgParams?.attachments || [];

    return (
        <div className={containerClassName} data-testid="order-activity-item">
            <div className="activity-box-content-wrap">
                <div className="activity-box-avatar-wrap flex-none !rounded-md hidden">
                    <Avatar imageUrl={activity.user?.imageUrl} image={activity.user?.image} name={actorName} />
                </div>
                <p className="text-gray500 text-xs leading-[1.5] font-normal flex-none w-[55px] whitespace-nowrap" data-testid="order-activity-item-date">
                    {time}
                </p>
                <div className="activity-box-text-wrap pl-[30px] min-w-0">
                    <div className="activity-box-title">
                        <span data-testid="order-activity-item-name">{actorName}</span>
                        {resolvedMsg && <ActivityDot />}
                        {resolvedMsg && (
                            <p className="activity-box-sub-title" data-testid="order-activity-item-description">
                                {resolvedMsg}
                            </p>
                        )}
                    </div>

                    {attachments.length > 0 && (
                        <PhotoProvider>
                            <div className="flex flex-wrap gap-2 mt-1.5">
                                {[...attachments]
                                    .sort((a) => (isImageFile(a.fileName) ? -1 : 1))
                                    .map((att: any, i: number) => {
                                        const fileUrl = att.fileUrl && att.file ? `${att.fileUrl}${att.file}` : undefined;
                                        const displayName = att.fileName || att.file || 'file';
                                        const isImg = isImageFile(att.fileName);

                                        if (isImg && fileUrl) {
                                            return (
                                                <div key={i} className="relative group  min-w-[46px] min-h-[46px] w-[46px] h-[46px] max-w-[46px] max-h-[46px]">
                                                    <PhotoView src={fileUrl}>
                                                        <img src={fileUrl} alt={displayName} className="table-profile-icn-circle-sm rounded-lg w-full h-full cursor-pointer" />
                                                    </PhotoView>
                                                </div>
                                            );
                                        }

                                        return (
                                            <TooltipCmp key={i} message={attachments.length > 1 ? displayName : null}>
                                                <div
                                                    className="relative group flex items-start gap-3 p-3 rounded-[10px] border border-borderPrimary bg-white max-w-[240px] cursor-pointer hover:opacity-90 transition-opacity"
                                                    onClick={() => fileUrl && window.open(fileUrl, '_blank')}
                                                >
                                                    <div className="relative flex-none">
                                                        {getFileExtension(att.fileName) === 'pdf' ? (
                                                            <img src={pdfAttachmentIcon} alt="PDF" className="w-8 h-8 object-contain flex-none" />
                                                        ) : (
                                                            getFileIcon(att.fileName)
                                                        )}
                                                        <span className="absolute -right-[5px] -bottom-1 flex items-center justify-center p-0.5 rounded-full bg-white border-[0.5px] border-utilityGray200">
                                                            <Check className="w-2.5 h-2.5 text-successSecondary" />
                                                        </span>
                                                    </div>
                                                    {attachments.length === 1 && (
                                                        <div className="flex-1 min-w-0 overflow-hidden">
                                                            <p className="text-xs leading-[1.5] font-normal text-grayLight900 truncate">{displayName}</p>
                                                            {att.fileSize ? <p className="text-[10px] leading-[14px] text-grayLight900 font-normal">{formatFileSize(att.fileSize)}</p> : null}
                                                        </div>
                                                    )}
                                                    {fileUrl && (
                                                        /* This tooltip flickered continuously on hover (client, 2026-10-09). Three things had to
                                                           line up, and all three are fixed here:
                                                           1. `absolute` used to sit on the BUTTON, so the button left the tooltip wrapper's flow and
                                                              the wrapper collapsed to a zero-size point inline after the filename. Tippy anchors to
                                                              that wrapper, so it placed the popper over the chip instead of above the button. The
                                                              positioning therefore belongs on the wrapper (childrenClassName), which is what gives
                                                              tippy the button's real 20px box.
                                                           2. app.scss forces `.tippy-popper { pointer-events: auto !important }` globally (interactive
                                                              tooltips need it). Tippy would otherwise set pointer-events:none on a non-interactive
                                                              popper precisely to prevent what happened next: the popper is appended to document.body,
                                                              so it is NOT a descendant of the trigger — the moment it covered any part of the button
                                                              the trigger got mouseleave, the tooltip closed, the button was uncovered, mouseenter
                                                              fired again. A 200ms loop, i.e. the blink.
                                                           3. The arrow is the remaining hole: it is pushed `bottom:-8px` into the gap and lives
                                                              OUTSIDE the content div that carries the keep-open handlers. Dropping it removes that
                                                              sliver AND moves the popper 8px further out via the existing `.tippy-no-arrow` rule.
                                                           `isHoverPersistent` then makes it correct by construction rather than by pixel margin: the
                                                           pointer reaching the popper cancels the close instead of racing it. */
                                                        <TooltipCmp
                                                            message={t('Common.Download')}
                                                            childrenClassName="absolute -top-1.5 -right-1.5 opacity-0 group-hover:opacity-100 transition-opacity"
                                                            isArrow={false}
                                                            isHoverPersistent
                                                        >
                                                            <button
                                                                type="button"
                                                                onClick={(e) => {
                                                                    e.stopPropagation();
                                                                    handleAttachmentDownload(fileUrl, displayName);
                                                                }}
                                                                className="flex items-center justify-center w-5 h-5 rounded-full bg-white shadow-md text-gray400 hover:text-primary"
                                                            >
                                                                <Download01 className="w-3 h-3" />
                                                            </button>
                                                        </TooltipCmp>
                                                    )}
                                                </div>
                                            </TooltipCmp>
                                        );
                                    })}
                            </div>
                        </PhotoProvider>
                    )}
                </div>
                <span className="absolute left-[70px] top-[2px] z-10">
                    <StatusDotProfileCmp dotColor="green" />
                </span>
            </div>
        </div>
    );
};

// ---------------------------------------------------------------------------
// Rate activity grouping — bug fix (user-reported): providing/updating several carrier rates on a
// quote in one sitting produced one timeline row per rate. Grouping now happens API-side
// (get-activities.js groups activities within a 30-minute window) — this component only renders
// whatever the API already grouped (activity.isRateGroup + msgParams.carriers), it does no
// grouping of its own.
// ---------------------------------------------------------------------------

// Grouped rate activity row — summary line ("<actor> provided N carrier rates for quote <code>")
// plus an expandable list of each carrier's name and price (basic details only).
const RateActivityGroupItem = ({ group, timezone, currentUserId }: { group: any; timezone?: string; currentUserId?: number }) => {
    const orgBranding = useOrgBranding();
    const [expanded, setExpanded] = useState(true);
    const actorName = buildActorName(group.user, currentUserId, group.activityName, orgBranding.name);
    const carriers: any[] = group.msgParams?.carriers || [];
    // quote_created_by_rule_set reads identically to quote_rate_created_by_user — the customer just
    // sees rates were provided, same as when a WAL user provides them manually. Rule-set mechanics
    // are internal detail, not shown here.
    const verb = group.activityName === 'quote_rate_updated_by_user' ? 'updated' : 'provided';
    const time = formatTime(group.createdAt, timezone);

    return (
        <div className="relative pb-5 flex" data-testid="order-activity-item-rate-group">
            <p className="text-gray500 text-xs leading-[1.5] font-normal flex-none w-[55px] whitespace-nowrap" data-testid="order-activity-item-date">
                {time}
            </p>

            <span className="absolute left-[70px] top-[2px] z-10">
                <StatusDotProfileCmp dotColor="green" />
            </span>

            <div className="pl-[42px] flex-1 min-w-0">
                <p className="text-[12px] leading-[18px] font-medium text-grayLight900 min-w-0 flex items-center gap-x-1" data-testid="order-activity-item-description">
                    <span className="truncate">
                        {actorName} {verb} {carriers.length} carrier rates
                    </span>
                    <button type="button" className="inline-flex items-center text-gray500 cursor-pointer" onClick={() => setExpanded((prev) => !prev)} data-testid="rate-group-toggle">
                        <ChevronDown className={`w-3.5 h-3.5 min-w-3.5 min-h-3.5 transition-transform duration-300 ${expanded ? 'rotate-180' : 'rotate-0'}`} />
                    </button>
                </p>

                {/* Grid-template-rows transition (not conditional render) so the box animates open/closed the same way CardCmp's accordion does. */}
                <div style={{ display: 'grid', gridTemplateRows: expanded ? '1fr' : '0fr', transition: 'grid-template-rows 300ms ease' }}>
                    <div className="overflow-hidden">
                        <div className="mt-2 rounded-lg border border-utilityGray200 divide-y divide-utilityGray200 overflow-hidden">
                            {carriers.map((carrier, idx) => {
                                const chargeLabel = carrier?.finalCharge != null ? `${getFormattedNumber(carrier.finalCharge, true)} ${carrier.currency || ''}`.trim() : '-';

                                return (
                                    <div key={`${carrier?.carrierName || 'carrier'}-${idx}`} className="flex items-center gap-x-2 text-xs px-2.5 py-2 bg-gray25" data-testid="rate-group-item">
                                        <span className="text-grayLight900 font-medium truncate flex-1 min-w-0">{carrier?.carrierName || 'Unknown carrier'}</span>
                                        <span className="text-grayLight600 flex-none flex items-center leading-[14px]">
                                            <CurrencyDollar className="w-3.5 h-3.5 min-w-3.5 min-h-3.5" />
                                            <span className="leading-[14px]">{chargeLabel}</span>
                                        </span>
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

// ---------------------------------------------------------------------------
// Grouping utilities
// ---------------------------------------------------------------------------

interface ActivityGroup {
    dateLabel: string;
    items: any[];
}

const groupActivitiesByDate = (activities: any[], timezone?: string): ActivityGroup[] => {
    const map = new Map<string, any[]>();

    activities.forEach((activity) => {
        const label = formatDateLabel(activity.createdAt, timezone);

        if (!map.has(label)) {
            map.set(label, []);
        }
        map.get(label)!.push(activity);
    });

    return Array.from(map.entries()).map(([dateLabel, items]) => ({ dateLabel, items }));
};

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------

interface ActivityFeedCmpProps {
    entityType: 'quote' | 'order';
    entityId: number;
    notePlaceholder?: string;
    wrapperClassName?: string;
    handleLoadMore: () => void;
    /**
     * When true, hides the post-input composer (avatar + textarea + emoji + send).
     * Used by public share-link pages where viewers can't author activities.
     */
    isReadOnly?: boolean;
    inputPosition?: 'top' | 'bottom';
    isRadarWidget?: boolean;
    isAccordionOpen?: boolean;
    /**
     * Chat-style feed: oldest at the top, NEWEST at the bottom. The feed opens scrolled to
     * the newest row, scrolling UP pages older rows in (holding the viewport still), and a
     * new post / socket row appended below is followed only when the user was already at
     * the tail. Redux keeps newest-first; only the rendering is flipped. Ported from WAL.
     */
    isNewestAtBottom?: boolean;
    /** 'sm' switches the activity cards to the roomier variant (16px padding, 40px avatar,
     *  13px text) used on the full-page Order and Quote detail activity tabs. */
    isActivityBox?: 'sm';
}

const ActivityFeedCmp = ({
    entityType,
    entityId,
    notePlaceholder = 'Add an activity or note',
    wrapperClassName = '',
    handleLoadMore,
    isReadOnly = false,
    inputPosition = 'top',
    isRadarWidget = false,
    isAccordionOpen = false,
    isNewestAtBottom = false,
}: ActivityFeedCmpProps) => {
    const { t } = useTranslation();
    const dispatch = useAppDispatch();
    const appearance = useSelector(appearanceSelector);
    const currentUser = useSelector(currentUserSelector);
    const activities = useAppSelector(activitiesData);
    const pagination = useAppSelector(activitiesPaginationData);
    const isLoading = useAppSelector(activitiesLoadingData);
    const isPostLoading = useAppSelector(activitiesPostLoadingData);

    const [note, setNote] = useState('');
    const [selectedFiles, setSelectedFiles] = useState<File[]>([]);
    const [showEmojiPicker, setShowEmojiPicker] = useState(false);
    const fileInputRef = useRef<HTMLInputElement>(null);
    const emojiPickerRef = useRef<HTMLDivElement>(null);
    const radarEmojiPickerRef = useRef<HTMLDivElement>(null);
    const sentinelRef = useRef<HTMLDivElement>(null);
    const isPostingRef = useRef(false);
    // Scrollable feed container — the IntersectionObserver root, so the sentinel fires when it
    // scrolls into view INSIDE the feed, not only when the page does (the sentinel lives in an
    // overflow-clipped region, so viewport-based intersection never fires for inner scrolling).
    const feedScrollRef = useRef<HTMLDivElement>(null);
    // isNewestAtBottom scroll bookkeeping.
    const prevScrollHeightRef = useRef<number | null>(null);
    const prevActivityCountRef = useRef(0);
    const hasAnchoredRef = useRef(false);
    const isAnchoringRef = useRef(false);
    // Lets the re-anchor effect tell a real accordion toggle from its own first run, which
    // would otherwise clear the flags the mount anchor had just set.
    const hasSeenAccordionStateRef = useRef(false);
    // Sampled on scroll, not at append time — once the new row is in the DOM the metrics already
    // include it, so "was the user at the bottom before this arrived" is unmeasurable then.
    const wasNearBottomRef = useRef(true);
    // Also state, so the observer effect re-attaches once anchoring lands: IntersectionObserver
    // only reports crossings, so a hit we suppressed never repeats on its own.
    const [hasAnchored, setHasAnchored] = useState(false);

    // Close emoji picker on outside click — covers page compose box and radar widget footer
    useEffect(() => {
        const handleClickOutside = (e: MouseEvent) => {
            const insidePage = emojiPickerRef.current?.contains(e.target as Node);
            const insideRadar = radarEmojiPickerRef.current?.contains(e.target as Node);

            if (!insidePage && !insideRadar) {
                setShowEmojiPicker(false);
            }
        };
        if (showEmojiPicker) document.addEventListener('mousedown', handleClickOutside);

        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, [showEmojiPicker]);

    useEffect(() => {
        const sentinel = sentinelRef.current;
        const root = feedScrollRef.current;
        if (!sentinel) return;

        const observer = new IntersectionObserver(
            (entries) => {
                if (entries[0].isIntersecting && pagination?.hasMore && !isLoading) {
                    if (isNewestAtBottom) {
                        // The sentinel sits at the TOP here, so it is on-screen until the initial
                        // scroll-to-bottom lands — firing before that pages in unrequested rows.
                        if (!hasAnchoredRef.current) return;

                        // Remember the height so the grow effect can hold the viewport still once
                        // the older rows are prepended above it.
                        if (feedScrollRef.current) {
                            prevScrollHeightRef.current = feedScrollRef.current.scrollHeight;
                        }
                    }

                    handleLoadMore();
                }
            },
            // Prefetch margin goes on the edge the user scrolls toward for older rows.
            { root: root ?? null, threshold: 0.1, rootMargin: isNewestAtBottom ? '200px 0px 0px 0px' : '0px 0px 200px 0px' }
        );

        observer.observe(sentinel);

        return () => observer.disconnect();
    }, [pagination?.hasMore, isLoading, handleLoadMore, isNewestAtBottom, hasAnchored]);

    // ── isNewestAtBottom scroll behaviour (ported from WAL ActivityFeedCmp) ────────────────
    useEffect(() => {
        if (!isNewestAtBottom) return;

        const el = feedScrollRef.current;
        if (!el) return;

        const onScroll = () => {
            // 80px slack so a partially visible last row still counts as "at the bottom".
            wasNearBottomRef.current = el.scrollHeight - el.scrollTop - el.clientHeight < 80;
        };

        // Not called eagerly: on mount scrollTop is 0 against a tall feed, which would read as
        // "not near bottom" and suppress the first tail-follow. The anchor sets the flag instead.
        el.addEventListener('scroll', onScroll, { passive: true });

        return () => el.removeEventListener('scroll', onScroll);
    }, [isNewestAtBottom, isLoading, activities.length === 0]);

    // Land on the newest row once the feed has content, and again when the accordion re-opens
    // (an open collapses the row area to h-0 and back, losing scrollTop). Guarded to the initial
    // settle so it never fights the user.
    useEffect(() => {
        if (!isNewestAtBottom) return;
        if (isLoading || activities.length === 0) return;

        if (hasAnchoredRef.current || isAnchoringRef.current) return;

        isAnchoringRef.current = true;

        requestAnimationFrame(() => {
            requestAnimationFrame(() => {
                if (feedScrollRef.current) {
                    feedScrollRef.current.scrollTop = feedScrollRef.current.scrollHeight;
                    // A scrollTop write does not reliably emit a scroll event, so set this directly.
                    wasNearBottomRef.current = true;
                }

                // Only after the scroll landed, so the top sentinel stays suppressed until then.
                isAnchoringRef.current = false;
                hasAnchoredRef.current = true;
                prevActivityCountRef.current = activities.length;
                setHasAnchored(true);
            });
        });
        // hasAnchored is a dep so a re-open (which flips it back to false) re-runs this and
        // re-anchors; the ref guard above still stops it from repeating on the same settle.
    }, [isNewestAtBottom, isLoading, activities.length, hasAnchored]);

    // Re-arm the anchor when the accordion OPENS. Two guards: the first run is skipped (it
    // would wipe the flags the mount anchor just set), and a collapse is skipped (the feed
    // stays mounted with hideContentOnCollapse={false}, so scrollTop survives and nothing
    // needs re-anchoring — clearing then would strand paging + tail-follow disabled).
    useEffect(() => {
        if (!isNewestAtBottom) return;

        if (!hasSeenAccordionStateRef.current) {
            hasSeenAccordionStateRef.current = true;

            return;
        }

        if (!isAccordionOpen) return;

        hasAnchoredRef.current = false;
        isAnchoringRef.current = false;
        setHasAnchored(false);
    }, [isAccordionOpen, isNewestAtBottom]);

    // The feed grows from both ends and each needs the opposite fix: a load-more batch prepends
    // above the viewport (hold position), a socket row or own post appends below (follow it).
    useEffect(() => {
        if (!isNewestAtBottom) return;

        const grew = activities.length > prevActivityCountRef.current;
        prevActivityCountRef.current = activities.length;

        const before = prevScrollHeightRef.current;
        prevScrollHeightRef.current = null;

        if (!grew || !hasAnchoredRef.current) return;

        const el = feedScrollRef.current;
        if (!el) return;

        if (before !== null) {
            const delta = el.scrollHeight - before;

            if (delta > 0) el.scrollTop += delta;

            return;
        }

        // Appended below — follow it only if the user was parked at the tail, not reading history.
        if (wasNearBottomRef.current) {
            el.scrollTo({ top: el.scrollHeight, behavior: 'smooth' });
        }
    }, [activities.length, isNewestAtBottom]);

    const handlePost = useCallback(async () => {
        if (isPostingRef.current) return;
        if (!note.trim() && selectedFiles.length === 0) return;

        isPostingRef.current = true;

        const formData = new FormData();
        formData.append('message', note.trim());

        // Append all selected files under the field name "attachments" (matches BE multer.array("attachments", 4))
        selectedFiles.forEach((file) => {
            formData.append('attachments', file);
        });

        dispatch(setActivitiesPostLoading(true));

        try {
            const response = await postQuoteActivity(entityType, entityId, formData);
            const newActivity = response?.data?.data?.activity || response?.data?.data || response?.data;

            if (newActivity) {
                dispatch(prependActivity(newActivity));
            }

            setNote('');
            setSelectedFiles([]);

            // Own post: always land on it, even if the user had scrolled up into history
            // (the grow effect alone only follows when they were already at the tail).
            if (isNewestAtBottom) {
                wasNearBottomRef.current = true;
                requestAnimationFrame(() => {
                    requestAnimationFrame(() => {
                        feedScrollRef.current?.scrollTo({ top: feedScrollRef.current.scrollHeight, behavior: 'smooth' });
                    });
                });
            }
        } catch (err: any) {
            WalToast.error(err?.response?.data?.message || 'Failed to post activity');
        } finally {
            dispatch(setActivitiesPostLoading(false));
            isPostingRef.current = false;
        }
    }, [entityId, note, selectedFiles, dispatch, isNewestAtBottom]);

    const handleKeyDown = (e: React.KeyboardEvent) => {
        if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault();

            if (!isPostLoading && (note?.trim() || selectedFiles.length > 0)) {
                handlePost();
            }
        }
    };

    const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
        const incoming = Array.from(e.target.files ?? []);
        e.target.value = '';
        if (!incoming.length) return;

        const invalid = incoming.find((f) => !ALLOWED_EXTENSIONS.includes(getFileExtension(f.name)));

        if (invalid) {
            const ext = getFileExtension(invalid.name);
            WalToast.error(ext ? `"${invalid.name}" has an unsupported file type (.${ext}).` : `"${invalid.name}" has no recognizable file extension.`);

            return;
        }

        if (incoming.some((f) => f.size > MAX_FILE_SIZE_BYTES)) {
            WalToast.error('File size should be less than 10MB.');

            return;
        }

        if (incoming.some((f) => f.size === 0)) {
            WalToast.error('File is empty.');

            return;
        }

        // Validate magic bytes to catch corrupted or misnamed files
        const corruptionFlags = await Promise.all(incoming.map((f) => checkFileCorrupted(f)));
        const corruptedFile = incoming.find((_, i) => corruptionFlags[i]);

        if (corruptedFile) {
            WalToast.error(`"${corruptedFile.name}" appears to be corrupted or invalid.`);

            return;
        }

        const totalAfter = selectedFiles.length + incoming.length;

        if (totalAfter > MAX_FILES) {
            WalToast.error(`You can attach a maximum of ${MAX_FILES} files.`);
            const remaining = MAX_FILES - selectedFiles.length;

            if (remaining > 0) {
                setSelectedFiles((prev) => [...prev, ...incoming.slice(0, remaining)]);
            }

            return;
        }

        setSelectedFiles((prev) => [...prev, ...incoming]);
    };

    const removeFile = (idx: number) => {
        setSelectedFiles((prev) => prev.filter((_, i) => i !== idx));
    };

    const timezone = appearance?.timezone;
    const rawGroups = groupActivitiesByDate(activities, timezone);
    // Flip both the group order and the rows inside each group; the Redux data stays newest-first.
    const groups = React.useMemo(() => (isNewestAtBottom ? [...rawGroups].reverse().map((g) => ({ ...g, items: [...g.items].reverse() })) : rawGroups), [rawGroups, isNewestAtBottom]);

    // Sentinel + "Loading more" — rendered below the rows in a newest-first feed, ABOVE them
    // when newest-at-bottom (older rows arrive at the top there).
    const loadMoreBlock = (
        <>
            <div ref={sentinelRef} className={isRadarWidget || isNewestAtBottom ? 'h-0.5' : 'h-4'} />
            {isLoading && pagination?.hasMore && (
                <div className="flex justify-center py-2">
                    <span className="text-[12px] text-gray500">Loading more...</span>
                </div>
            )}
        </>
    );

    // Build id→name lookup from Redux services list (fetched in AppLayout on login, lives in state.basic.services)
    const allServices = useAppSelector((state: any) => state.basic?.services || []);
    const serviceMap: Record<number, string> = React.useMemo(() => Object.fromEntries(allServices.map((s: any) => [s.id, s.name])), [allServices]);

    return (
        <div className={`flex flex-col ${isRadarWidget ? 'flex-1 min-h-0' : 'overflow-auto flex-1'} ${wrapperClassName}`} data-testid="order-activity">
            <div
                className={`flex-1 flex flex-col ${
                    isRadarWidget
                        ? '!py-0 overflow-auto custom-scrollbar-v2 !p-0 !border-none'
                        : `bg-white border border-utilityGray200 rounded-xl shadow-xs py-6 [&>*]:px-6 ${inputPosition === 'bottom' ? 'overflow-hidden' : 'overflow-auto scrollbar-hide'} ${
                              isReadOnly ? 'px-0 !py-0 [&>*]:!px-4' : 'pt-4 lg:py-6 [&>*]:px-4 lg:[&>*]:px-6'
                          }`
                }`}
            >
                {/* Post input box — hidden in radar widget and read-only mode */}
                {!isReadOnly && !isRadarWidget ? (
                    <div ref={emojiPickerRef} className={`relative ${inputPosition === 'bottom' ? 'order-last flex-none mt-3' : 'mb-3'}`}>
                        <div className="bg-white border border-utilityGray200 rounded-lg shadow-xs flex flex-col flex-1">
                            <div className="flex-1 flex flex-col">
                                <div className="flex items-start gap-2 px-3 py-2">
                                    {/* Current user avatar */}
                                    <div className="flex-none cursor-default">
                                        {currentUser?.image ? (
                                            <img
                                                src={`${currentUser.imageUrl}${currentUser.image}`}
                                                alt={`${currentUser.firstName} ${currentUser.lastName}`}
                                                title={`${currentUser.firstName} ${currentUser.lastName}`}
                                                onError={onError}
                                                className="w-10 h-10 rounded-[10px] border border-utilityBlack border-opacity-[0.08] object-contain flex-none"
                                            />
                                        ) : (
                                            <div className="w-10 h-10 rounded-[10px] border border-utilityBlack border-opacity-[0.08] flex-none flex items-center justify-center uppercase bg-primary text-white text-sm">
                                                {getShortName(`${currentUser?.firstName ?? ''} ${currentUser?.lastName ?? ''}`)}
                                            </div>
                                        )}
                                    </div>
                                    <InputTextArea
                                        inputName="note"
                                        value={note}
                                        onChangeFunc={(e) => {
                                            const inputValue = e?.target?.value || '';
                                            const trimmedValue = inputValue?.trim();

                                            if (trimmedValue === '' && inputValue?.length > 0) {
                                                setNote('');
                                            } else {
                                                setNote(inputValue?.trimStart());
                                            }
                                        }}
                                        handleKeyDown={handleKeyDown}
                                        rows={2}
                                        placeholder={notePlaceholder}
                                        // !h-auto overrides the .form_control height so `rows` drives the box height.
                                        // !py-[7px] puts the first line where WAL's does, top-aligned beside the 40px avatar.
                                        className="!border-none !shadow-none focus:!shadow-none !ring-0 focus:!ring-0 !h-auto !py-[7px]"
                                        parentClassName="w-full"
                                        maxLength={200}
                                    />
                                </div>

                                {/* Selected file previews */}
                                {selectedFiles.length > 0 && (
                                    <div className="px-3 pb-2 border-t border-borderPrimary">
                                        <div className="flex items-center justify-between mb-1.5 pt-2">
                                            <p className="text-xs text-gray500">
                                                {selectedFiles.length} {selectedFiles.length === 1 ? 'file' : 'files'} selected
                                            </p>
                                            <button type="button" className="text-xs text-danger500 hover:text-danger600 font-medium transition-colors" onClick={() => setSelectedFiles([])}>
                                                {selectedFiles.length === 1 ? 'Remove' : 'Remove All'}
                                            </button>
                                        </div>
                                        <div className="flex flex-wrap gap-2">
                                            {selectedFiles.map((file, idx) => {
                                                const isImg = isImageFile(file.name);
                                                const localPreviewUrl = isImg ? URL.createObjectURL(file) : null;

                                                return (
                                                    <div key={idx} className="flex items-center gap-2 pl-1.5 pr-2 py-1.5 rounded-lg border border-utilityGray200 bg-white max-w-[200px]">
                                                        {isImg && localPreviewUrl ? (
                                                            <img src={localPreviewUrl} alt={file.name} className="w-9 h-9 rounded object-cover flex-none" />
                                                        ) : (
                                                            getFileIcon(file.name)
                                                        )}
                                                        <span className="text-xs text-grayLight900 truncate flex-1 min-w-0">{file.name}</span>
                                                        <button type="button" className="flex-none text-gray400 hover:text-danger500 transition-colors ml-1" onClick={() => removeFile(idx)}>
                                                            <XClose className="w-3 h-3" />
                                                        </button>
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    </div>
                                )}

                                <div className="flex flex-wrap items-center justify-between gap-3 bg-gray50 px-3 py-2 rounded-b-lg">
                                    <div className="flex items-center gap-2 text-gray400">
                                        {/* Emoji picker trigger */}
                                        <TooltipCmp message="Add Emoji">
                                            <button type="button" className="transition-colors" onClick={() => setShowEmojiPicker((prev) => !prev)}>
                                                <FaceSmile className="w-4 h-4" />
                                            </button>
                                        </TooltipCmp>
                                        {/* File attachment button */}
                                        <TooltipCmp message="Attach File">
                                            <button type="button" className="transition-colors" onClick={() => fileInputRef.current?.click()}>
                                                <Paperclip className="w-4 h-4" />
                                            </button>
                                        </TooltipCmp>
                                        {/* Hidden multi-file input */}
                                        <input
                                            ref={fileInputRef}
                                            type="file"
                                            multiple
                                            className="hidden"
                                            accept="image/*,.pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.txt,.csv"
                                            onChange={handleFileChange}
                                        />
                                    </div>
                                    <ButtonCmp type="button" disabled={!note?.trim() && selectedFiles.length === 0} loading={isPostLoading} className="btn_secondary_black" onClick={handlePost}>
                                        Post
                                    </ButtonCmp>
                                </div>
                            </div>
                        </div>

                        {/* Emoji picker dropdown */}
                        {showEmojiPicker && (
                            <div className="absolute top-full left-0 z-[99]">
                                <EmojiPicker
                                    onSelect={(name) => {
                                        const char = getEmoji(name);
                                        if (char) setNote((prev) => prev + char);
                                        setShowEmojiPicker(false);
                                    }}
                                />
                            </div>
                        )}
                    </div>
                ) : !isRadarWidget ? (
                    <>
                        {' '}
                        <div className="table-top-header mb-3">
                            <div className="table-left-wrap ">
                                <div className={`table-title-wrap`}>
                                    <h5 className="text-textSecondary text-sm font-semibold table-title">Latest Activity</h5>
                                </div>
                            </div>
                        </div>
                    </>
                ) : null}

                {/* Timeline feed */}
                <div
                    ref={feedScrollRef}
                    className={`flex flex-col flex-1 overflow-auto custom-scrollbar-v2 ${inputPosition === 'bottom' ? 'order-first !px-4' : ''} ${
                        isReadOnly ? 'px-4 lg:px-6 max-lg:max-h-[314px] lg:max-h-[calc(100vh-332px)]' : 'px-0'
                    } ${isRadarWidget ? `${!isAccordionOpen ? 'max-h-[calc(100vh-245px)]' : 'max-h-[300px]'} overflow-auto custom-scrollbar-v2` : ''}`}
                >
                    {isLoading && activities.length === 0 ? (
                        <ActivityFeedSkeleton />
                    ) : activities.length === 0 ? (
                        <NotFoundUI title="No activities yet" desc="Activities and notes for this quote will appear here." containerClassName="flex-1" testId="activity-feed-empty" />
                    ) : (
                        <>
                            {isNewestAtBottom && loadMoreBlock}

                            {groups.map((group, groupIndex) => (
                                <React.Fragment key={group.dateLabel}>
                                    <div className="flex items-center justify-between gap-x-2 pb-3 mb-3 border-b border-utilityGray200 sticky top-0 z-[12] bg-white" data-testid="order-activity-group">
                                        <p
                                            className="relative text-xs leading-[1.5] font-medium text-textSecondary"
                                            // dateLabel is produced by formatDateLabel (a moment format), never user input —
                                            // getDateWithSuffixFormat only wraps its ordinal suffix in <sup>.
                                            dangerouslySetInnerHTML={{ __html: getDateWithSuffixFormat(group.dateLabel) }}
                                        />
                                        <p className="text-gray500 text-xs leading-[1.5] font-normal">{t('Common.Activity_Count', { count: group.items.length })}</p>
                                    </div>
                                    <div className="relative">
                                        <div
                                            className={`relative ${
                                                !isRadarWidget && !isNewestAtBottom && groupIndex === groups.length - 1
                                                    ? '[&>*:last-child]:bg-white [&>*:last-child]:!relative [&>*:last-child]:z-10'
                                                    : ''
                                            }`}
                                        >
                                            {group.items.map((activity) => {
                                                if (activity.isRateGroup) {
                                                    return <RateActivityGroupItem key={activity.id} group={activity} timezone={timezone} currentUserId={currentUser?.id} />;
                                                }

                                                // Company-attributed system activities (e.g. quote_created_by_rule_set) render as a
                                                // normal timeline row (actor = "Western Alliance"), not the separate integration
                                                // card — they belong in the same unified feed as every other quote activity.
                                                const isIntegration = (activity?.userType === 'system' || activity?.integration) && !COMPANY_ATTRIBUTED_ACTIVITY_NAMES.has(activity?.activityName);
                                                const isPost = !!activity?.isChatMessage || activity?.activityStatus === 'post';

                                                if (isIntegration) {
                                                    return <IntegrationCard key={activity.id} activity={activity} timezone={timezone} cardClass="relative pb-5" />;
                                                }

                                                if (isPost) {
                                                    return <PostCard key={activity.id} activity={activity} timezone={timezone} currentUserId={currentUser?.id} containerClassName="relative pb-5" />;
                                                }

                                                return (
                                                    <UserActivityItem
                                                        key={activity.id}
                                                        activity={activity}
                                                        timezone={timezone}
                                                        serviceMap={serviceMap}
                                                        currentUserId={currentUser?.id}
                                                        isReadOnly={isReadOnly}
                                                    />
                                                );
                                            })}
                                        </div>

                                        <div
                                            className={`absolute left-[75px] -top-3 w-[2px] flex items-center justify-center rounded bg-borderPrimary ${
                                                isRadarWidget || isNewestAtBottom ? 'bottom-0' : 'bottom-4'
                                            }`}
                                        />
                                    </div>
                                </React.Fragment>
                            ))}

                            {/* Sentinel — triggers auto-load when scrolled into view */}
                            {!isNewestAtBottom && loadMoreBlock}
                        </>
                    )}
                </div>
            </div>

            {/* Radar widget compact footer (outside scroll section) — hidden in read-only mode */}
            {isRadarWidget && !isReadOnly && (
                <div className="relative border-t mt-auto border-utilityGray200 shadow-[0_-1px_4px_0_rgba(16,24,40,0.05)]">
                    {/* Selected file previews */}
                    {selectedFiles.length > 0 && (
                        <div className="px-3 pb-2 border-b border-utilityGray200">
                            <div className="flex items-center justify-between mb-1.5 pt-1.5">
                                <p className="text-xs text-gray500">
                                    {selectedFiles.length} {selectedFiles.length === 1 ? 'file' : 'files'} selected
                                </p>
                                <button type="button" className="text-xs text-danger500 hover:text-danger600 font-medium transition-colors" onClick={() => setSelectedFiles([])}>
                                    {selectedFiles.length === 1 ? 'Remove' : 'Remove All'}
                                </button>
                            </div>
                            <div className="flex flex-wrap gap-2">
                                {selectedFiles.map((file, idx) => {
                                    const isImg = isImageFile(file.name);
                                    const previewUrl = isImg ? URL.createObjectURL(file) : null;

                                    return (
                                        <div key={file.name + file.size} className="flex items-center gap-2 pl-1.5 pr-2 py-1.5 rounded-lg border border-utilityGray200 bg-white max-w-[200px]">
                                            {isImg && previewUrl ? <img src={previewUrl} alt={file.name} className="w-9 h-9 rounded object-cover flex-none" /> : getFileIcon(file.name)}
                                            <span className="text-xs text-grayLight900 truncate flex-1 min-w-0">{file.name}</span>
                                            <button type="button" className="flex-none text-gray400 hover:text-danger500 transition-colors ml-1" onClick={() => removeFile(idx)}>
                                                <XClose className="w-3 h-3" />
                                            </button>
                                        </div>
                                    );
                                })}
                            </div>
                        </div>
                    )}

                    <div className="flex items-center w-full">
                        {/* Input row */}
                        <div className="flex items-start px-4 py-3 flex-1">
                            <div className="flex-none cursor-default">
                                {currentUser?.image ? (
                                    <img
                                        src={`${currentUser.imageUrl}${currentUser.image}`}
                                        className="w-8 h-8 rounded-[10px] border border-utilityBlack border-opacity-[0.08] flex-none object-contain"
                                        alt={`${currentUser.firstName} ${currentUser.lastName}`}
                                        title={`${currentUser.firstName} ${currentUser.lastName}`}
                                        onError={onError}
                                    />
                                ) : (
                                    <div className="w-8 h-8 rounded-[10px] border border-utilityBlack border-opacity-[0.08] flex-none flex items-center justify-center uppercase bg-primary text-white text-[8px]">
                                        {getShortName(`${currentUser?.firstName ?? ''} ${currentUser?.lastName ?? ''}`)}
                                    </div>
                                )}
                            </div>
                            <InputTextArea
                                inputName="note"
                                value={note}
                                onChangeFunc={(e) => {
                                    const inputValue = e?.target?.value || '';
                                    const trimmedValue = inputValue?.trim();

                                    if (trimmedValue === '' && inputValue?.length > 0) {
                                        setNote('');
                                    } else {
                                        setNote(inputValue?.trimStart());
                                    }
                                }}
                                handleKeyDown={handleKeyDown}
                                rows={1}
                                placeholder={notePlaceholder}
                                className="!border-none !shadow-none focus:!shadow-none !ring-0 focus:!ring-0 !py-[7px]"
                                parentClassName="w-full"
                                maxLength={2000}
                            />
                        </div>

                        {/* Action icons */}
                        <div className="flex items-center gap-2 px-4 py-3 flex-none">
                            <div ref={radarEmojiPickerRef} className="relative">
                                <TooltipCmp message="Add Emoji" childrenClassName="flex">
                                    <button type="button" className="text-gray400 hover:text-primary transition-colors" onClick={() => setShowEmojiPicker((prev) => !prev)}>
                                        <FaceSmile className="w-4 h-4" />
                                    </button>
                                </TooltipCmp>
                                {showEmojiPicker && (
                                    <EmojiPicker
                                        className="bottom-full right-0 !z-[99] !left-auto"
                                        onSelect={(name) => {
                                            const char = getEmoji(name);
                                            if (char) setNote((prev) => prev + char);
                                            setShowEmojiPicker(false);
                                        }}
                                    />
                                )}
                            </div>
                            <TooltipCmp message="Attach File" childrenClassName="flex">
                                <button type="button" className="text-gray400 hover:text-primary transition-colors" onClick={() => fileInputRef.current?.click()}>
                                    <Paperclip className="w-4 h-4" />
                                </button>
                            </TooltipCmp>
                            <input ref={fileInputRef} type="file" multiple className="hidden" accept="image/*,.pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.txt,.csv" onChange={handleFileChange} />
                            <TooltipCmp message="Send Message" childrenClassName="flex">
                                <ButtonCmp
                                    className="btn_secondary_black"
                                    size="square-md"
                                    disabled={isPostLoading || (!note?.trim() && selectedFiles.length === 0)}
                                    loading={isPostLoading}
                                    onClick={handlePost}
                                >
                                    <Send01 className="w-4 h-4" />
                                </ButtonCmp>
                            </TooltipCmp>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default ActivityFeedCmp;
